// src/components/tokenPage/overview/SupplyUnlocksCard.tsx

import React from "react";
import { useAllocationAndUnlockProgress } from "@/hooks/tokenPage/utilityValue/useAllocationAndUnlockProgress";
import { useUnlockSchedule } from "@/hooks/tokenPage/utilityValue/useUnlockSchedule";
import { KpiBox } from "../shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import { formatDateLabel } from "@/utils/charts/dataUtils";

interface Props {
  tokenId: string;
}

const SupplyUnlocksCard: React.FC<Props> = ({ tokenId }) => {
  const { data: unlockData, isLoading: unlockLoading } = useAllocationAndUnlockProgress(tokenId);
  const { data: scheduleData, isLoading: scheduleLoading } = useUnlockSchedule(tokenId);

  const isLoading = unlockLoading || scheduleLoading;

  // Check if we have any unlock data
  const hasUnlockData = unlockData?.data?.circulatingSupply != null || 
                         unlockData?.data?.maxSupply != null || 
                         unlockData?.data?.totalSupply != null ||
                         (scheduleData?.data?.chart?.length ?? 0) > 0;
  
  if (!isLoading && !hasUnlockData) {
    return null;
  }

  const circulatingSupplyRaw = unlockData?.data?.circulatingSupply;
  const maxSupplyRaw = unlockData?.data?.maxSupply;
  const totalSupplyRaw = unlockData?.data?.totalSupply;

  const circulatingSupply = circulatingSupplyRaw ? Number(circulatingSupplyRaw) : null;
  const maxSupply = maxSupplyRaw ? Number(maxSupplyRaw) : (totalSupplyRaw ? Number(totalSupplyRaw) : null);

  const circulatingPct = (circulatingSupply != null && maxSupply != null && maxSupply > 0)
    ? ((circulatingSupply / maxSupply) * 100)
    : null;

  const schedule = scheduleData?.data || {};
  const nextUnlockRaw = unlockData?.data?.nextUnlock;

  const unlockEvents = (schedule.chart || []).map((point: { date: number; unlocked: number | string }) => {
    const normalizeTimestamp = (ts: number) => ts > 1e10 ? ts / 1000 : ts;
    const timestamp = normalizeTimestamp(point.date);
    return {
      date: formatDateLabel(timestamp * 1000, 'medium'),
      timestamp,
      value: Number(point.unlocked ?? 0),
    };
  });

  const now = Date.now() / 1000;
  const futureEvents = unlockEvents.filter(e => e.timestamp > now);
  const nextUnlockEvent = futureEvents[0];

  let nextUnlockAmount: string | null = null;
  let nextUnlockAmountNumber = 0;
  type NextUnlockPayload = { amount?: number | string; tokens?: number | string } | null | undefined;
  const nu = nextUnlockRaw as NextUnlockPayload;
  const rawAmount = nu?.amount ?? nu?.tokens;
  const amount = Number(rawAmount ?? 0);
  if (amount > 0) {
    nextUnlockAmountNumber = amount;
    nextUnlockAmount = amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  const supplyForPct = maxSupply || totalSupplyRaw ? Number(maxSupply || totalSupplyRaw) : null;
  const nextUnlockPct = (nextUnlockAmountNumber > 0 && supplyForPct != null && supplyForPct > 0)
    ? ((nextUnlockAmountNumber / supplyForPct) * 100)
    : null;

  const formatNextUnlock = () => {
    if (!nextUnlockEvent || !nextUnlockAmount) {
      return "N/A";
    }
    return `${nextUnlockEvent.date} • ${nextUnlockAmount}`;
  };

  // Check if we have any individual metrics to show
  const hasCirculatingSupply = circulatingSupply != null;
  const hasMaxSupply = maxSupply != null;
  const hasCirculatingPct = circulatingPct != null && !isNaN(circulatingPct);
  const hasNextUnlock = nextUnlockEvent && nextUnlockAmount;

  return (
    <DataCard
      title="Supply & Unlocks"
      subtitle="Circulating, caps and unlock progress"
      icon="vault"
      iconColor="text-purple-400"
      isLoading={isLoading}
      loadingVariant="stats"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {hasCirculatingSupply && <KpiBox label="Circulating Supply" value={circulatingSupply.toLocaleString("en-US", { maximumFractionDigits: 0 })} />}
        {hasMaxSupply && <KpiBox label="Max Supply" value={maxSupply.toLocaleString("en-US", { maximumFractionDigits: 0 })} />}
        {hasCirculatingPct && <KpiBox label="% Circulating" value={`${circulatingPct.toFixed(1)}%`} />}
        {hasNextUnlock && <KpiBox label="Next Unlock" value={formatNextUnlock()} sub={nextUnlockPct != null ? `${nextUnlockPct.toFixed(2)}% of total supply` : undefined} />}
      </div>
    </DataCard>
  );
};

export default SupplyUnlocksCard;
